import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  ArchiveBoxIcon, 
  PencilIcon, 
  TrashIcon, 
  MagnifyingGlassIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { productApi } from '../../api/productApi';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    stock: '',
    barcode: '',
    hsnCode: '',
    expiryDate: '',
    imageUrl: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const res = await productApi.getProducts();
      setProducts(res.data.data || []);
    } catch (error) {
      console.error('Failed to fetch products', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        description: product.description || '',
        price: product.price,
        stock: product.stock,
        barcode: product.barcode || '',
        hsnCode: product.hsnCode || '',
        expiryDate: product.expiryDate ? new Date(product.expiryDate).toISOString().split('T')[0] : '',
        imageUrl: product.imageUrl || ''
      });
    } else {
      setEditingProduct(null);
      setFormData({ name: '', description: '', price: '', stock: '', barcode: '', hsnCode: '', expiryDate: '', imageUrl: '' });
    }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        stock: Number(formData.stock || 0),
        barcode: formData.barcode,
        hsnCode: formData.hsnCode,
        expiryDate: formData.expiryDate || null,
        imageUrl: formData.imageUrl
      };

      if (editingProduct) {
        await productApi.updateProduct(editingProduct._id, payload);
        toast.success('Product updated successfully');
      } else {
        await productApi.addProduct(payload);
        toast.success('Product added successfully');
      }
      
      setIsModalOpen(false);
      fetchProducts();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await productApi.deleteProduct(id);
        toast.success('Product deleted');
        fetchProducts();
      } catch (error) {
        console.error(error);
        toast.error('Failed to delete product');
      }
    }
  };

  if (loading && products.length === 0) {
    return (
      <div className="w-full h-96 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#dc343b]"></div>
      </div>
    );
  }

  return (
    <div className="w-full pb-10 max-w-7xl mx-auto space-y-6">
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">Inventory</h1>
          <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">Manage your products, pricing, and stock.</p>
        </div>
        <button 
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 bg-[#dc343b] text-white px-5 py-2.5 rounded-xl font-bold text-[13px] hover:bg-[#b91c1c] transition-colors shadow-sm shadow-red-500/20"
        >
          <PlusIcon className="h-5 w-5" />
          Add Product
        </button>
      </div>

      {/* Search Bar */}
      <div className="relative w-full max-w-md">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          className="block w-full pl-10 pr-3 py-3 border border-gray-200 dark:border-gray-700 rounded-xl leading-5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#dc343b] focus:border-[#dc343b] sm:text-[14px] shadow-sm transition-colors"
          placeholder="Search products by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Products Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <div key={product._id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 p-5 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-md transition-shadow relative group">
              <div className="flex justify-between items-start mb-3">
                <div className="p-3 bg-red-50 dark:bg-red-900/20 text-[#dc343b] dark:text-red-400 rounded-xl">
                  <ArchiveBoxIcon className="h-6 w-6" />
                </div>
                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-2">
                  <button onClick={() => handleOpenModal(product)} className="p-1.5 text-gray-400 hover:text-blue-600 bg-gray-50 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded-lg transition-colors">
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(product._id)} className="p-1.5 text-gray-400 hover:text-red-600 bg-gray-50 dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/40 rounded-lg transition-colors">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <h3 className="text-[16px] font-bold text-gray-900 dark:text-white mb-1 truncate">{product.name}</h3>
              <p className="text-[13px] text-gray-500 dark:text-gray-400 mb-4 line-clamp-2 min-h-[40px]">
                {product.description || 'No description provided.'}
              </p>
              
              <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-700">
                <div>
                  <p className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">Price</p>
                  <p className="text-[16px] font-extrabold text-[#dc343b] dark:text-red-400">₹{product.price.toLocaleString()}</p>
                </div>
                <div className="text-right">
                  <p className="text-[11px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-0.5">In Stock</p>
                  <p className={`text-[15px] font-bold ${product.stock > 0 ? 'text-green-600 dark:text-green-500' : 'text-red-500 dark:text-red-400'}`}>
                    {product.stock}
                  </p>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="col-span-full py-20 text-center bg-gray-50/50 dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700">
            <ArchiveBoxIcon className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <h3 className="text-[15px] font-bold text-gray-900 dark:text-white">No products found</h3>
            <p className="text-[14px] text-gray-500 dark:text-gray-400 mt-1">Get started by adding your first product to inventory.</p>
            <button 
              onClick={() => handleOpenModal()}
              className="mt-5 inline-flex items-center gap-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-lg font-bold text-[13px] hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
            >
              <PlusIcon className="h-4 w-4" />
              Add Product
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
          <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-2xl relative z-10 animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-gray-700">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-[18px] font-bold text-gray-900 dark:text-white">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Product Name *</label>
                <input
                  type="text"
                  required
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                  placeholder="e.g. Rice 1kg"
                  value={formData.name}
                  onChange={e => setFormData({...formData, name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                  placeholder="Optional details..."
                  rows="2"
                  value={formData.description}
                  onChange={e => setFormData({...formData, description: e.target.value})}
                ></textarea>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Selling Price (₹) *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                    placeholder="0.00"
                    value={formData.price}
                    onChange={e => setFormData({...formData, price: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Stock Quantity</label>
                  <input
                    type="number"
                    min="0"
                    className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                    placeholder="0"
                    value={formData.stock}
                    onChange={e => setFormData({...formData, stock: e.target.value})}
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Barcode / SKU (Optional)</label>
                <input
                  type="text"
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                  placeholder="Scan or type barcode"
                  value={formData.barcode}
                  onChange={e => setFormData({...formData, barcode: e.target.value})}
                />
              </div>

              <div>
                <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Image URL (Optional)</label>
                <input
                  type="text"
                  className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                  placeholder="https://example.com/image.jpg"
                  value={formData.imageUrl}
                  onChange={e => setFormData({...formData, imageUrl: e.target.value})}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">HSN / SAC Code</label>
                  <input
                    type="text"
                    className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                    placeholder="e.g. 1006 (Optional)"
                    value={formData.hsnCode}
                    onChange={e => setFormData({...formData, hsnCode: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-[13px] font-bold text-gray-700 dark:text-gray-300 mb-1">Expiry Date (Optional)</label>
                  <input
                    type="date"
                    className="block w-full rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3 text-[14px] focus:border-[#dc343b] focus:ring-1 focus:ring-[#dc343b] bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white focus:bg-white dark:focus:bg-gray-800 transition-colors"
                    value={formData.expiryDate}
                    onChange={e => setFormData({...formData, expiryDate: e.target.value})}
                  />
                </div>
              </div>

              <div className="pt-4">
                <button
                  type="submit"
                  disabled={saving}
                  className="w-full flex justify-center items-center bg-[#0f172a] text-white px-4 py-3.5 rounded-xl font-bold text-[14px] hover:bg-slate-800 transition-colors shadow-md disabled:opacity-70"
                >
                  {saving ? 'Saving...' : (editingProduct ? 'Update Product' : 'Save Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default Products;
